/**
 * VoicemailDownloadManager.ts
 * Talkroute
 *
 * Created by Vel Genov on 3/10/19
 * Copyright (c) 2019 Talkroute. All right reserved.
 */

import path from "path";

import appConfig from "../config/index";

import validateArray from "./arrayValidator";
import FilePathHelper from "./filePathHelper";
const VOICEMAIL_USER_FOLDER = "/Voicemails/";

/**
 * Manages Voicemail files on the file system
 */
export default class VoicemailDownloadManager {
  public electron?: any = appConfig.electron;
  public filePath: string;
  public userDataPath: string;
  constructor(opts) {
    if (opts.userDataPath) {
      // Use the `configName` property to set the file name and path.join to bring it all together as a string
      this.filePath = path.join(
        new FilePathHelper().userDataPath,
        VOICEMAIL_USER_FOLDER,
        opts.configName
      );
    } else {
      // Bundle file
      this.filePath = path.join(__dirname, opts.configName);
    }

    if (appConfig.electron) {
      this.userDataPath = (
        appConfig.electron.app || appConfig.electron.remote.app
      ).getPath("userData");
    } else {
      this.userDataPath = "/userData";
    }
    if (!appConfig.fileStorage?.exists(this.userDataPath)) {
      appConfig.fileStorage?.createPath(this.userDataPath);
    }
  }

  /**
   * Stores this Voicemail file to the user data folder
   * The file is not copied if it already exists in the destination
   *
   * @returns A promise. The promise resolves once the file copy actions is completed.
   * The promise returns either an absolute file path to the new Voicemail file or an error
   */
  storeInUserData = () => {
    // The voicemail file name
    const fileName = path.basename(this.filePath);
    // The path where all Voicemails are stored
    const voicemailStorePath = `${this.userDataPath}${VOICEMAIL_USER_FOLDER}`;
    // The Voicemails path + this Voicemail's file name
    const absoluteFilePath = `${voicemailStorePath}${fileName}`;

    const dataPromise = new Promise((resolve, reject) => {
      if (appConfig.fileStorage?.exists(absoluteFilePath)) {
        resolve(absoluteFilePath);
      } else {
        // If this is the 1st run, the voicemails folder will not be here
        // Check and create if not here
        if (!appConfig.fileStorage?.exists(voicemailStorePath)) {
          appConfig.fileStorage?.createPath(voicemailStorePath);
        }
        // Existing files will be overwritten
        appConfig.fileStorage?.copyAsync(
          this.filePath,
          absoluteFilePath,
          (error) => {
            if (!error) {
              resolve(absoluteFilePath);
            } else {
              reject(error);
            }
          }
        );
      }
    });

    return dataPromise;
  };

  /**
   * Deletes this voicemail. Logs an error if there is a problem or the file doesn't exist
   *
   */
  deleteVoicemail = () => {
    const { filePath } = this;
    try {
      appConfig.fileStorage?.remove(filePath);
    } catch (e) {
    }
  };

  deleteAllVoicemailFiles = () => {
    const voicemailDataPath = path.join(
      this.userDataPath,
      VOICEMAIL_USER_FOLDER
    );
    const files = appConfig.fileStorage?.getList(voicemailDataPath);
    if (files && validateArray(files)) {
      files.forEach((element) => {
        const voicemailDataManager = new VoicemailDownloadManager({
          userDataPath: true,
          configName: element,
        });

        voicemailDataManager.deleteVoicemail();
      });
    }
  };
}
